/* ==========================================================================
   Lalla — predictive-search.js
   <predictive-search>: Shopify predictive search (/search/suggest) rendered
   by the "predictive-search" section. Combobox + listbox keyboard pattern:
   ArrowUp/ArrowDown move through options, Enter opens the active one.
   ========================================================================== */

(() => {
  if (customElements.get('predictive-search')) return;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.popular = this.querySelector('[data-search-popular]');
      this.cache = new Map();
      this.options = [];
      this.activeIndex = -1;

      const debounce = (window.Lalla && window.Lalla.debounce) || ((fn) => fn);
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('reset', () => {
        setTimeout(() => {
          this.reset();
          this.input.focus();
        });
      });
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      });
      this.results.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.setActive(this.options.indexOf(option), { scroll: false });
      });

      if (this.input.value.trim()) this.onInput();
    }

    get query() {
      return this.input.value.trim();
    }

    async onInput() {
      const query = this.query;
      if (this.clearButton) this.clearButton.hidden = !query;
      if (!query) {
        this.reset();
        return;
      }
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      this.announce(this.dataset.loadingText);

      const params = new URLSearchParams({
        q: query,
        section_id: this.dataset.section,
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });

      try {
        const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.querySelector('[data-predictive-root]');
        const html = root ? root.outerHTML : '';
        this.cache.set(query, html);
        if (query === this.query) this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.reset();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.results.hidden = !html;
      if (this.popular) this.popular.hidden = Boolean(html);
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', String(this.options.length > 0));

      const total = Number(this.results.querySelector('[data-predictive-root]')?.dataset.total || 0);
      if (total > 0) {
        const template = total === 1 ? this.dataset.countOne : this.dataset.countOther;
        this.announce((template || '').replace('[count]', total).replace('{{ count }}', total));
      } else {
        const empty = this.results.querySelector('.predictive-results__empty-title');
        this.announce(empty ? empty.textContent : '');
      }
    }

    reset() {
      if (this.controller) this.controller.abort();
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.popular) this.popular.hidden = false;
      if (this.clearButton) this.clearButton.hidden = true;
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.announce('');
    }

    onKeydown(event) {
      if (!this.options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (this.activeIndex + step + this.options.length) % this.options.length;
        this.setActive(next);
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        event.preventDefault();
        window.location.assign(this.options[this.activeIndex].href);
      }
    }

    setActive(index, { scroll = true } = {}) {
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      this.activeIndex = index;
      const option = this.options[index];
      if (!option) return;
      this.input.setAttribute('aria-activedescendant', option.id);
      if (scroll) option.scrollIntoView({ block: 'nearest' });
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
